import { msalInstance } from '../auth/msalInstance';
import { DATAVERSE_URL, dataverseScopes } from '../auth/config';
import { reAcquireToken, signIn } from '../auth/loginHelpers';

/**
 * Thin Dataverse Web API client. Mirrors Pipeline's pattern — silent
 * token acquisition with single-flight re-auth fallback, plus
 * GET/POST/PATCH/DELETE helpers. Throws on non-2xx.
 */

/**
 * Typed Dataverse failure, so callers can distinguish "you're not set up"
 * (403) from a real fault. See design-system/ACCESS-EMPTY-STATE-PATTERN.md.
 */
export class DataverseError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'DataverseError';
    this.status = status;
  }
}

/** True when a failure is a missing-Dataverse-role 403 (a setup gap, not a bug). */
export function isAccessDenied(err: unknown): boolean {
  return err instanceof DataverseError && err.status === 403;
}

/** Shared copy naming the fix, per the pattern. */
export const ACCESS_DENIED_MESSAGE =
  'Your access isn’t fully set up, so this can’t load. Ask an admin to add you to the “GAQ App Users” group.';

const API_VERSION = 'v9.2';
const STANDARD_HEADERS = {
  'OData-MaxVersion': '4.0',
  'OData-Version': '4.0',
  Accept: 'application/json',
  'Content-Type': 'application/json; charset=utf-8',
  Prefer: 'odata.include-annotations="*"',
};

let reauthInFlight = false;

function triggerReauth(go: () => Promise<void>): void {
  if (reauthInFlight) return;
  reauthInFlight = true;
  go().catch((e) => {
    reauthInFlight = false;
    console.error('Re-authentication failed to start', e);
  });
}

async function getToken(): Promise<string> {
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    triggerReauth(async () => {
      await signIn(dataverseScopes);
    });
    throw new Error('Re-authenticating: no active MSAL account');
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: dataverseScopes,
      account,
    });
    return result.accessToken;
  } catch (err) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw err;
    }
    triggerReauth(async () => {
      await reAcquireToken(dataverseScopes, account);
    });
    throw new Error('Re-authenticating: silent token acquisition failed');
  }
}

export async function odataGet<T = unknown>(
  entitySet: string,
  query = ''
): Promise<{ value: T[]; '@odata.count'?: number; '@odata.nextLink'?: string }> {
  const token = await getToken();
  const url = `${DATAVERSE_URL}/api/data/${API_VERSION}/${entitySet}${query}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { ...STANDARD_HEADERS, Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new DataverseError(
      response.status,
      `Dataverse ${response.status} on ${entitySet}: ${text.slice(0, 300)}`
    );
  }
  return response.json();
}

export async function odataPost<T = unknown>(
  entitySet: string,
  body: Record<string, unknown>
): Promise<T> {
  const token = await getToken();
  const url = `${DATAVERSE_URL}/api/data/${API_VERSION}/${entitySet}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...STANDARD_HEADERS,
      Authorization: `Bearer ${token}`,
      Prefer: STANDARD_HEADERS.Prefer + ',return=representation',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new DataverseError(
      response.status,
      `Dataverse ${response.status} POST ${entitySet}: ${text.slice(0, 300)}`
    );
  }
  return (await response.json()) as T;
}

export async function odataPatch(
  entitySet: string,
  id: string,
  body: Record<string, unknown>
): Promise<void> {
  const token = await getToken();
  const url = `${DATAVERSE_URL}/api/data/${API_VERSION}/${entitySet}(${id})`;
  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      ...STANDARD_HEADERS,
      Authorization: `Bearer ${token}`,
      'If-Match': '*',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new DataverseError(
      response.status,
      `Dataverse ${response.status} PATCH ${entitySet}: ${text.slice(0, 300)}`
    );
  }
}

export async function odataDelete(entitySet: string, id: string): Promise<void> {
  const token = await getToken();
  const url = `${DATAVERSE_URL}/api/data/${API_VERSION}/${entitySet}(${id})`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { ...STANDARD_HEADERS, Authorization: `Bearer ${token}` },
  });
  if (!response.ok && response.status !== 404) {
    const text = await response.text().catch(() => '');
    throw new DataverseError(
      response.status,
      `Dataverse ${response.status} DELETE ${entitySet}: ${text.slice(0, 300)}`
    );
  }
}

/**
 * Escape a value for use INSIDE a single-quoted OData string literal in a URL:
 *   1. double single-quotes  — OData literal escaping (O'Brien → O''Brien)
 *   2. percent-encode        — so URL-reserved characters in the value
 *      (& + # ? / and spaces) can't corrupt the query string.
 *
 * Step 2 matters: a product like "Offices & Surgeries Package" previously
 * broke the library filter — the & was read as a query-parameter separator,
 * giving a 400 "query parameter not supported". Every call site puts the
 * result inside single quotes in a $filter, so encoding here fixes them all.
 * (Numeric/GUID filters don't use this and are unaffected.)
 */
export function odataEscape(value: string): string {
  return encodeURIComponent(value.replace(/'/g, "''"));
}
